'use client';

import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import type { RepSession } from '@/lib/types/rep-session';
import { captureClientPosthogEvent } from '@/lib/utils/capture-client-posthog';
import { useCobrowse } from './cobrowse-provider';

const CONSENT_KEY_PREFIX = 'red-experience-cobrowse-consent-accepted:';

export function hasLocalConsent(opportunityId: string): boolean {
  try {
    return localStorage.getItem(`${CONSENT_KEY_PREFIX}${opportunityId}`) === '1';
  } catch {
    return false;
  }
}

function markLocalConsent(opportunityId: string) {
  try {
    localStorage.setItem(`${CONSENT_KEY_PREFIX}${opportunityId}`, '1');
  } catch {
    // private browsing or storage disabled
  }
}

interface RepSessionContextValue {
  sessionToken: string | null;
  sessionData: RepSession | null;
  sessionValidated: boolean;
  sessionError: string | null;
  isConnectedToRep: boolean;
  showConsentModal: boolean;
  handleConsentAccept: () => Promise<void>;
  handleConsentDecline: () => Promise<void>;
}

const RepSessionContext = createContext<RepSessionContextValue | null>(null);

export function useRepSessionContext(): RepSessionContextValue {
  const ctx = useContext(RepSessionContext);
  if (!ctx) throw new Error('useRepSessionContext must be used within RepSessionProvider');
  return ctx;
}

interface RepSessionProviderProps {
  children: ReactNode;
  opportunityId: string;
  sessionToken: string | null;
}

export function RepSessionProvider({
  children,
  opportunityId,
  sessionToken: initialToken,
}: RepSessionProviderProps) {
  const { createCode, endSession: endCobrowseSession } = useCobrowse();

  const [sessionToken, setSessionToken] = useState<string | null>(initialToken);
  const [sessionData, setSessionDataRaw] = useState<RepSession | null>(null);
  const [sessionValidated, setSessionValidated] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [isConnectedToRep, setIsConnectedToRep] = useState(false);
  const lastScreenShareRequestedAtRef = useRef<string | null>(null);
  const dismissedScreenShareRequestedAtRef = useRef<string | null>(null);
  const isConnectingRef = useRef(false);
  const deadTokensRef = useRef<Set<string>>(new Set());

  const setSessionData = useCallback((incoming: RepSession) => {
    setSessionDataRaw(prev => {
      if (
        prev &&
        prev.status === incoming.status &&
        prev.cobrowseCode === incoming.cobrowseCode &&
        prev.cobrowseSessionId === incoming.cobrowseSessionId &&
        prev.clientLastSeenAt === incoming.clientLastSeenAt &&
        prev.currentStep === incoming.currentStep &&
        prev.screenShareRequestedAt === incoming.screenShareRequestedAt
      ) {
        return prev;
      }
      return incoming;
    });
  }, []);

  useEffect(() => {
    if (initialToken && initialToken !== sessionToken && !deadTokensRef.current.has(initialToken)) {
      setSessionToken(initialToken);
      setSessionValidated(false);
      setSessionDataRaw(null);
      setIsConnectedToRep(false);
      setSessionError(null);
      lastScreenShareRequestedAtRef.current = null;
      dismissedScreenShareRequestedAtRef.current = null;
    }
  }, [initialToken, sessionToken]);

  const resetSessionState = useCallback((deadToken?: string | null) => {
    if (deadToken) {
      deadTokensRef.current.add(deadToken);
      try {
        sessionStorage.removeItem(`red-rep-session:${opportunityId}`);
      } catch { /* ignore */ }
    }
    setSessionToken(null);
    setSessionValidated(false);
    setIsConnectedToRep(false);
    setSessionDataRaw(null);
    setSessionError(null);
    lastScreenShareRequestedAtRef.current = null;
    dismissedScreenShareRequestedAtRef.current = null;
    setShowConsentModal(false);
    endCobrowseSession();
  }, [endCobrowseSession, opportunityId]);

  const connectScreenShare = useCallback(async (mode: 'manual' | 'auto' = 'manual') => {
    const token = sessionToken;
    if (!token) throw new Error('Session not available. Please refresh the page.');
    if (isConnectingRef.current) return;
    isConnectingRef.current = true;

    try {
      captureClientPosthogEvent('rep_session_cobrowse_code_start', {
        opportunityId,
        mode,
        sessionId: sessionData?.id,
        status: sessionData?.status,
      });
      const result = await Promise.race([
        createCode(),
        new Promise<null>((_, reject) =>
          setTimeout(() => reject(new Error('Timed out starting screen share')), 15000),
        ),
      ]);

      if (!result) throw new Error('Failed to create screen share session');

      const { code, sessionId } = result;
      captureClientPosthogEvent('rep_session_cobrowse_code_created', {
        opportunityId,
        mode,
        sessionId: sessionData?.id,
        hasCode: Boolean(code),
        hasCobrowseSessionId: Boolean(sessionId),
      });

      const res = await fetch('/api/client/session/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          transition: 'active',
          cobrowseCode: code,
          cobrowseSessionId: sessionId,
        }),
      });

      if (!res.ok) {
        const errData = await res.json();
        captureClientPosthogEvent('rep_session_cobrowse_validate_failed', {
          opportunityId,
          mode,
          sessionId: sessionData?.id,
          statusCode: res.status,
          error: errData.error || 'Failed to update session',
        });
        throw new Error(errData.error || 'Failed to update session');
      }

      const data = await res.json();
      if (data.session) setSessionData(data.session);
      captureClientPosthogEvent('rep_session_cobrowse_connected', {
        opportunityId,
        mode,
        sessionId: data.session?.id || sessionData?.id,
        status: data.session?.status,
        hasCobrowseCode: Boolean(data.session?.cobrowseCode),
        hasCobrowseSessionId: Boolean(data.session?.cobrowseSessionId),
      });
      markLocalConsent(opportunityId);
      setShowConsentModal(false);
      setIsConnectedToRep(true);
    } catch (err) {
      captureClientPosthogEvent('rep_session_cobrowse_code_failed', {
        opportunityId,
        mode,
        sessionId: sessionData?.id,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      console.error('[RepSession] Failed to connect screen share:', err);
      if (mode === 'auto') {
        setShowConsentModal(true);
        return;
      }
      throw err;
    } finally {
      isConnectingRef.current = false;
    }
  }, [createCode, sessionToken, opportunityId, setSessionData, sessionData?.id, sessionData?.status]);

  const connectScreenShareRef = useRef(connectScreenShare);
  connectScreenShareRef.current = connectScreenShare;
  const resetSessionStateRef = useRef(resetSessionState);
  resetSessionStateRef.current = resetSessionState;

  useEffect(() => {
    if (!sessionData || isConnectedToRep || showConsentModal || isConnectingRef.current) {
      return;
    }

    if (sessionData.status !== 'screen_share_requested' || !sessionData.screenShareRequestedAt) {
      return;
    }

    if (dismissedScreenShareRequestedAtRef.current === sessionData.screenShareRequestedAt) {
      return;
    }

    const consentAlreadyAccepted =
      Boolean(sessionData.consentAcceptedAt) || hasLocalConsent(opportunityId);

    if (consentAlreadyAccepted) {
      captureClientPosthogEvent('rep_session_consent_auto_accept_attempted', {
        opportunityId,
        sessionId: sessionData.id,
        status: sessionData.status,
      });
      connectScreenShareRef.current('auto').catch(() => {});
      return;
    }

    captureClientPosthogEvent('rep_session_consent_modal_shown', {
      opportunityId,
      sessionId: sessionData.id,
      status: sessionData.status,
      screenShareRequestedAt: sessionData.screenShareRequestedAt,
    });
    setShowConsentModal(true);
  }, [
    sessionData,
    isConnectedToRep,
    showConsentModal,
    opportunityId,
  ]);

  // Step 1: Initial validation + heartbeat
  useEffect(() => {
    if (!sessionToken) return;
    const tokenSnapshot = sessionToken;
    let cancelled = false;

    async function validate() {
      try {
        const res = await fetch('/api/client/session/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenSnapshot, heartbeat: true }),
        });
        const data = await res.json();
        if (cancelled) return;

        if (data.valid && data.session) {
          setSessionData(data.session);
          setSessionValidated(true);
          captureClientPosthogEvent('rep_session_token_validated', {
            opportunityId,
            sessionId: data.session.id,
            status: data.session.status,
            hasSessionToken: Boolean(data.session.sessionToken),
          });

          if (data.session.status === 'screen_share_requested' && data.session.screenShareRequestedAt) {
            lastScreenShareRequestedAtRef.current = data.session.screenShareRequestedAt;
            const consentAlreadyAccepted =
              Boolean(data.session.consentAcceptedAt) || hasLocalConsent(opportunityId);

            if (consentAlreadyAccepted) {
              captureClientPosthogEvent('rep_session_consent_auto_accept_attempted', {
                opportunityId,
                sessionId: data.session.id,
                status: data.session.status,
              });
              connectScreenShareRef.current('auto').catch(() => {});
            } else {
              captureClientPosthogEvent('rep_session_consent_modal_shown', {
                opportunityId,
                sessionId: data.session.id,
                status: data.session.status,
                screenShareRequestedAt: data.session.screenShareRequestedAt,
              });
              setShowConsentModal(true);
            }
          }
        } else {
          if (data.errorCode === 'ENDED' || data.errorCode === 'EXPIRED') {
            resetSessionStateRef.current(tokenSnapshot);
            return;
          }
          captureClientPosthogEvent('rep_session_token_invalid', {
            opportunityId,
            errorCode: data.errorCode,
            error: data.error,
          });
          setSessionError(data.error || 'Invalid session');
        }
      } catch {
        captureClientPosthogEvent('rep_session_token_validation_failed', { opportunityId });
        if (!cancelled) setSessionError('Failed to validate session');
      }
    }

    validate();
    return () => { cancelled = true; };
  }, [sessionToken, opportunityId, setSessionData]);

  // Step 1.5: Poll by opportunityId to discover rep-initiated sessions without a URL token
  useEffect(() => {
    if (sessionToken || isConnectedToRep) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    let pollInterval = 3000;

    const pollForSession = async () => {
      if (document.visibilityState === 'hidden') {
        timeoutId = setTimeout(pollForSession, pollInterval);
        return;
      }
      try {
        const res = await fetch(`/api/client/session?opportunityId=${encodeURIComponent(opportunityId)}`);
        const data = await res.json();

        if (data.success && data.session?.sessionToken) {
          const { status } = data.session;
          if (['pending', 'viewing', 'screen_share_requested', 'active'].includes(status)) {
            captureClientPosthogEvent('rep_session_discovered_by_opportunity', {
              opportunityId,
              sessionId: data.session.id,
              status,
            });
            setSessionToken(data.session.sessionToken);
            setSessionData(data.session);
            return;
          }
        }

        pollInterval = Math.min(pollInterval * 1.5, 10000);
        timeoutId = setTimeout(pollForSession, pollInterval);
      } catch {
        pollInterval = Math.min(pollInterval * 2, 10000);
        timeoutId = setTimeout(pollForSession, pollInterval);
      }
    };

    timeoutId = setTimeout(pollForSession, 1000);
    return () => clearTimeout(timeoutId);
  }, [opportunityId, sessionToken, isConnectedToRep, setSessionData]);

  // Step 2: Poll for session status changes (screen share requests)
  useEffect(() => {
    if (!sessionToken || !sessionValidated || isConnectedToRep) return;

    const tokenSnapshot = sessionToken;
    let timeoutId: ReturnType<typeof setTimeout>;
    let pollInterval = 2000;

    const poll = async () => {
      if (document.visibilityState === 'hidden') {
        timeoutId = setTimeout(poll, pollInterval);
        return;
      }
      try {
        const res = await fetch('/api/client/session/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenSnapshot, heartbeat: true }),
        });
        const data = await res.json();

        if (!data.valid) {
          if (data.errorCode === 'ENDED' || data.errorCode === 'EXPIRED') {
            resetSessionStateRef.current(tokenSnapshot);
            return;
          }
          timeoutId = setTimeout(poll, pollInterval);
          return;
        }

        if (data.session) {
          setSessionData(data.session);
          const status = data.session.status;
          const requestedAt = data.session.screenShareRequestedAt;

          if (
            status === 'screen_share_requested' &&
            requestedAt &&
            requestedAt !== lastScreenShareRequestedAtRef.current
          ) {
            lastScreenShareRequestedAtRef.current = requestedAt;
            const consentAlreadyAccepted =
              Boolean(data.session.consentAcceptedAt) || hasLocalConsent(opportunityId);

            if (consentAlreadyAccepted) {
              connectScreenShareRef.current('auto').catch(() => {});
            } else {
              setIsConnectedToRep(false);
              setShowConsentModal(true);
            }
          }

          if (status === 'active' && !isConnectedToRep) {
            setIsConnectedToRep(true);
          }
        }
      } catch {
        // keep polling
      }

      pollInterval = Math.min(pollInterval * 1.2, 5000);
      timeoutId = setTimeout(poll, pollInterval);
    };

    timeoutId = setTimeout(poll, pollInterval);
    return () => clearTimeout(timeoutId);
  }, [sessionToken, sessionValidated, isConnectedToRep, opportunityId, setSessionData]);

  // Step 3: Health poll while connected — detect rep-ended sessions
  useEffect(() => {
    if (!sessionToken || !isConnectedToRep) return;

    const token = sessionToken;
    async function pollHealth() {
      if (document.visibilityState === 'hidden') return;
      try {
        const res = await fetch('/api/client/session/validate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, heartbeat: true }),
        });
        const data = await res.json();

        if (!data.valid && (data.errorCode === 'ENDED' || data.errorCode === 'EXPIRED')) {
          resetSessionStateRef.current(token);
          return;
        }

        if (data.session) {
          setSessionData(data.session);

          if (
            data.session.status === 'screen_share_requested' &&
            data.session.screenShareRequestedAt &&
            data.session.screenShareRequestedAt !== lastScreenShareRequestedAtRef.current
          ) {
            lastScreenShareRequestedAtRef.current = data.session.screenShareRequestedAt;
            setIsConnectedToRep(false);
            connectScreenShareRef.current('auto').catch(() => {});
          }
        }
      } catch {
        // transient error, keep polling
      }
    }

    pollHealth();
    const intervalId = setInterval(pollHealth, 4000);
    return () => clearInterval(intervalId);
  }, [sessionToken, isConnectedToRep, setSessionData]);

  const handleConsentAccept = useCallback(async () => {
    captureClientPosthogEvent('rep_session_consent_accepted', {
      opportunityId,
      sessionId: sessionData?.id,
      status: sessionData?.status,
    });
    await connectScreenShare('manual');
  }, [connectScreenShare, opportunityId, sessionData?.id, sessionData?.status]);

  const handleConsentDecline = useCallback(async () => {
    setShowConsentModal(false);

    if (!sessionToken || sessionData?.status !== 'screen_share_requested') {
      return;
    }

    captureClientPosthogEvent('rep_session_consent_declined', {
      opportunityId,
      sessionId: sessionData.id,
      status: sessionData.status,
    });
    dismissedScreenShareRequestedAtRef.current = sessionData.screenShareRequestedAt ?? null;

    try {
      const res = await fetch('/api/client/session/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: sessionToken, transition: 'viewing' }),
      });

      const data = await res.json();
      if (res.ok && data.session) {
        setSessionData(data.session);
      }
    } catch {
      // best-effort: if this fails, the polling loop will reconcile state
    }
  }, [opportunityId, sessionData, sessionToken, setSessionData]);

  const value = useMemo<RepSessionContextValue>(() => ({
    sessionToken,
    sessionData,
    sessionValidated,
    sessionError,
    isConnectedToRep,
    showConsentModal,
    handleConsentAccept,
    handleConsentDecline,
  }), [
    sessionToken, sessionData, sessionValidated, sessionError,
    isConnectedToRep, showConsentModal, handleConsentAccept, handleConsentDecline,
  ]);

  return (
    <RepSessionContext.Provider value={value}>
      {children}
    </RepSessionContext.Provider>
  );
}
